import { useAuth } from "@via/hooks/useAuth";
import {
  useRegisterUrlInvite,
  useVerifyEmail,
} from "@via/pages/authentication/self-service-signup/authentication.api.tsx";
import EmailConfirmation from "@via/pages/authentication/self-service-signup/email-confirmation.tsx";
import { EmailRegisterForm } from "@via/pages/authentication/self-service-signup/email-register-form.tsx";
import { ROUTES } from "@via/utils/constants.tsx";
import { signUpOptions } from "@via/utils/form-validators.tsx";
import { RegisterRequestBody } from "common";
import { type JSX, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useValidateInviteUrl } from "@via/pages/organization/organization.api.tsx";
import { LogoSpinner } from "@via/components/loading/loading";
import { GenericError } from "@via/components/error";
import PendingApprovalScreen from "@via/pages/authentication/url-invite-signup/pending-approval-screen.tsx";
import UrlInviteAccountInfoForm from "@via/pages/authentication/url-invite-signup/url-invite-account-info-form.tsx";

enum UrlInviteStep {
  Loading = 0,
  InvalidUrl = -1,
  EmailForm = 1,
  EmailConfirmation = 2,
  AccountInfo = 3,
  PendingApproval = 4,
}

export default function UrlInviteSignup(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<UrlInviteStep>(UrlInviteStep.Loading);

  const {
    data: validateResult,
    isLoading: isValidateLoading,
    isError: isValidateError,
  } = useValidateInviteUrl(slug);

  const {
    mutateAsync: registerEmail,
    isPending: isRegisterEmailLoading,
    isSuccess: isRegisterEmailSuccess,
  } = useRegisterUrlInvite();

  const {
    mutate: verifyEmail,
    isPending: isVerifyEmailLoading,
    isSuccess: isVerifyEmailSuccess,
    isError: isVerifyEmailError,
  } = useVerifyEmail();

  const {
    watch,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterRequestBody>({
    mode: "onChange",
  });

  const emailValue = watch("email");

  // Redirect logged-in users
  useEffect(() => {
    if (user?.session) {
      navigate(ROUTES.ACTIVITY);
    }
  }, [user?.session, navigate]);

  // Determine step from validation
  useEffect(() => {
    if (!slug) {
      setStep(UrlInviteStep.InvalidUrl);
      return;
    }
    if (isValidateLoading) {
      setStep(UrlInviteStep.Loading);
      return;
    }
    if (isValidateError || validateResult?.valid === false) {
      setStep(UrlInviteStep.InvalidUrl);
      return;
    }
    if (validateResult?.valid === true && step === UrlInviteStep.Loading) {
      setStep(UrlInviteStep.EmailForm);
    }
  }, [isValidateLoading, isValidateError, validateResult, slug, step]);

  useEffect(() => {
    if (isRegisterEmailSuccess) {
      setStep(UrlInviteStep.EmailConfirmation);
    }
  }, [isRegisterEmailSuccess]);

  useEffect(() => {
    if (isVerifyEmailSuccess) {
      setStep(UrlInviteStep.AccountInfo);
    }
  }, [isVerifyEmailSuccess]);

  const handleEmailRegister = async (
    form: RegisterRequestBody
  ): Promise<void> => {
    await registerEmail(form);
  };

  const handleResendConfirmation = async () => {
    await registerEmail({ email: emailValue, isResend: true });
  };

  const handleVerifyConfirmation = (value: string) => {
    verifyEmail({ email: emailValue, verificationCode: value });
  };

  const handleAccountSuccess = () => {
    setStep(UrlInviteStep.PendingApproval);
  };

  const invitationDetails = validateResult?.valid
    ? { organizationName: validateResult.organizationName }
    : undefined;

  if (step === UrlInviteStep.Loading) {
    return (
      <div className="via-loading-spinner">
        <LogoSpinner />
      </div>
    );
  }

  if (step === UrlInviteStep.InvalidUrl) {
    return (
      <div className="via-generic-error">
        <GenericError
          message={
            !slug
              ? "Invalid invite link"
              : "This invite link is invalid or has expired."
          }
        />
        <Link
          to={ROUTES.LOGIN}
          className="mt-4 text-sm font-semibold text-indigo-600 hover:text-indigo-500">
          Go to Login
        </Link>
      </div>
    );
  }

  if (step === UrlInviteStep.PendingApproval && validateResult?.valid) {
    return (
      <div className="flex flex-1 flex-col py-6 sm:px-8 lg:px-8">
        <div className="mx-8 lg:mx-24 flex h-full flex-row items-center justify-center">
          <PendingApprovalScreen
            organizationName={validateResult.organizationName}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col py-6 sm:px-8 lg:px-8">
      <div className="mx-8 lg:mx-24 flex h-full flex-row items-center justify-center">
        <div className="flex flex-col items-center justify-center">
          {step === UrlInviteStep.EmailForm ? (
            <EmailRegisterForm
              register={register}
              errors={errors}
              isRegisterEmailLoading={isRegisterEmailLoading}
              onSubmit={handleSubmit(handleEmailRegister)}
              signUpOptions={signUpOptions}
              invitationDetails={invitationDetails}
            />
          ) : step === UrlInviteStep.EmailConfirmation ? (
            <EmailConfirmation
              email={emailValue}
              onVerify={handleVerifyConfirmation}
              onResend={handleResendConfirmation}
              isError={isVerifyEmailError}
              isLoading={isVerifyEmailLoading}
            />
          ) : step === UrlInviteStep.AccountInfo && slug ? (
            <UrlInviteAccountInfoForm
              slug={slug}
              email={emailValue}
              onSuccess={handleAccountSuccess}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
